import { CalendarDate, fromDate, getLocalTimeZone, toCalendarDate } from '@internationalized/date';
import {
  addMonths,
  endOfDay,
  format,
  isBefore,
  isSameMonth,
  isSameYear,
  isEqual,
  isValid,
  lastDayOfMonth,
  lastDayOfYear,
  parse,
  startOfDay,
  startOfWeek,
  startOfMonth,
  endOfWeek,
  endOfMonth,
  addDays,
  isSameDay,
  isWithinInterval,
  min,
  max,
  addYears,
  subYears,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  Locale,
  isToday,
  subMonths,
  differenceInHours,
  differenceInDays,
  parseISO,
} from 'date-fns';
import capitalize from 'lodash/capitalize';
import { useTranslation } from 'react-i18next';
import { defaultDateFormat } from '~/consts/date';
import { DateRange } from '~/types/date';
import { formatDateForDisplay, formatDateForRange } from '~/utils/formatDate';

export const useFormatRange = () => {
  const { t } = useTranslation('inputs', { keyPrefix: 'date_range_filters' });
  const now = new Date();
  return (from: Date | undefined, to: Date | undefined) => {
    if (from && to) {
      if (from.getDate() === 1 && isSameMonth(from, to) && lastDayOfMonth(from).getDate() === to.getDate()) {
        if (isSameMonth(now, from)) {
          return t('this_month');
        }
        if (isSameMonth(subMonths(now, 1), from)) {
          return t('previous_month');
        }
        if (isSameMonth(addMonths(now, 1), from)) {
          return t('next_month');
        }
        return capitalize(format(from, 'MMMM yyyy'));
      }
      if (
        from.getDate() === 1 &&
        from.getMonth() === 0 &&
        isSameYear(from, to) &&
        lastDayOfYear(from).getDate() === to.getDate() &&
        lastDayOfYear(from).getMonth() === to.getMonth()
      ) {
        if (isSameYear(now, to)) {
          return t('this_year');
        }
        if (isSameYear(subYears(now, 1), to)) {
          return t('previous_year');
        }

        return format(from, 'yyyy');
      }
      if (isFirstDayOfMonth(from) && isLastDayOfMonth(to)) {
        return `${capitalize(format(from, 'MMM yy'))} - ${capitalize(format(to, 'MMM yy'))}`;
      }
      return `${formatDateForRange(from)} - ${formatDateForRange(to)}`;
    }
    if (from) {
      if (isToday(from)) {
        return t('from_today');
      }
      if (from.getDate() === 1) {
        return t('date_range_since', { date: capitalize(format(from, 'MMMM yy')) });
      }
      return `${formatDateForDisplay(from)} -`;
    }
    if (to) {
      if (lastDayOfMonth(to).getDate() === to.getDate()) {
        return t('date_range_until', { date: capitalize(format(to, 'MMMM yy')) });
      }
      return `— ${formatDateForRange(to)}`;
    }
    return '';
  };
};

export const useFormatMonthRange = () => {
  const { t } = useTranslation('inputs', { keyPrefix: 'month_range_filters' });
  const now = new Date();
  return (from: Date | undefined, to: Date | undefined) => {
    if (from && to) {
      if (from.getDate() === 1 && isSameMonth(from, to) && lastDayOfMonth(from).getDate() === to.getDate()) {
        if (isSameMonth(now, from)) {
          return t('this_month');
        }
        if (isSameMonth(subMonths(now, 1), from)) {
          return t('previous_month');
        }
        if (isSameMonth(addMonths(now, 1), from)) {
          return t('next_month');
        }
        return capitalize(format(from, 'MMMM yyyy'));
      }
      if (
        from.getDate() === 1 &&
        from.getMonth() === 0 &&
        isSameYear(from, to) &&
        lastDayOfYear(from).getDate() === to.getDate() &&
        lastDayOfYear(from).getMonth() === to.getMonth()
      ) {
        if (isSameYear(now, to)) {
          return t('this_year');
        }
        if (isSameYear(subYears(now, 1), to)) {
          return t('previous_year');
        }

        return format(from, 'yyyy');
      }
      if (isFirstDayOfMonth(from) && isLastDayOfMonth(to)) {
        if (isSameMonth(to, now)) {
          if (isSameMonth(from, subMonths(now, 2))) return t('last_3_months');
          if (isSameMonth(from, subMonths(now, 5))) return t('last_6_months');
          if (isSameMonth(from, subMonths(now, 11))) return t('last_12_months');
          if (isSameMonth(from, subMonths(now, 23))) return t('last_24_months');
        }
        return `${capitalize(format(from, 'MMM yy'))} - ${capitalize(format(to, 'MMM yy'))}`;
      }
      return `${formatDateForRange(from)} — ${formatDateForRange(to)}`;
    }
    if (from) {
      if (isToday(from)) {
        return t('from_today');
      }
      if (from.getDate() === 1) {
        return t('date_range_since', { date: capitalize(format(from, 'MMMM yy')) });
      }
      return `${formatDateForDisplay(from)} —`;
    }
    if (to) {
      if (lastDayOfMonth(to).getDate() === to.getDate()) {
        return t('date_range_until', { date: capitalize(format(to, 'MMMM yy')) });
      }
      return `— ${formatDateForRange(to)}`;
    }
    return '';
  };
};

export const iterateMonthsBetweenDates =
  (startDate: Date, endDate: Date) => (iterationCallback: (date: Date) => void) => {
    let suppDate = startDate;
    do {
      iterationCallback(suppDate);
      suppDate = addMonths(suppDate, 1);
    } while (isBefore(suppDate, endDate) || isEqual(suppDate, endDate));
  };

export const resolveDateRange = (period: string) => {
  const [startDate, endDate] = period.split('/').map((d) => parse(d, defaultDateFormat, new Date()));

  if (!isValid(startDate))
    throw new Error('Start date is invalid. The date should respect `yyyy-MM-dd` and should be timeless.');

  if (!isValid(endDate))
    throw new Error('End date is invalid. The date should respect `yyyy-MM-dd` and should be timeless.');

  return {
    startDate: startOfDay(startDate!),
    endDate: endOfDay(endDate!),
  };
};

export const chunks = <T>(array: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(array.length / size) }, (_v, i) => array.slice(i * size, i * size + size));

// Date
export const getDaysInCalendarMonth = (date: Date, locale?: Locale) => {
  const startWeek = startOfWeek(startOfMonth(date), { locale });
  const endWeek = endOfWeek(endOfMonth(date), { locale });
  const days = [];
  for (let curr = startWeek; isBefore(curr, endWeek);) {
    days.push(curr);
    curr = addDays(curr, 1);
  }
  return days;
};

export const isStartOfRange = ({ startDate }: DateRange, day: Date) =>
  (startDate && isSameDay(day, startDate)) as boolean;

export const isEndOfRange = ({ endDate }: DateRange, day: Date) => (endDate && isSameDay(day, endDate)) as boolean;

export const inDateRange = ({ startDate, endDate }: DateRange, day: Date) => {
  if (startDate && endDate) {
    return Boolean(
      isWithinInterval(day, { start: startDate, end: endDate }) || isSameDay(day, startDate) || isSameDay(day, endDate),
    );
  }

  if (startDate && !endDate) {
    return Boolean(
      isWithinInterval(day, { start: startDate, end: addYears(startDate, 100) }) || isSameDay(day, startDate),
    );
  }

  if (!startDate && endDate) {
    return Boolean(isWithinInterval(day, { start: subYears(endDate, 100), end: endDate }) || isSameDay(day, endDate));
  }

  return false;
};

export const getValidatedMonths = (range: DateRange, minDate: Date, maxDate: Date) => {
  const { startDate, endDate } = range;
  if (startDate && endDate) {
    const newStart = max([startDate, minDate]);
    const newEnd = min([endDate, maxDate]);

    return [newStart, isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd];
  }
  return [startDate, endDate];
};

export const dateToCalendarDate = (date: Date): CalendarDate => toCalendarDate(fromDate(date, getLocalTimeZone()));

export const hoursUntilEndOfDay = () => {
  const now = new Date();
  const end = endOfDay(now);

  return differenceInHours(end, now);
};

export const daysUntilEndOfWeek = () => {
  const now = new Date();
  const end = endOfWeek(now, { weekStartsOn: 1 });

  return differenceInDays(end, now);
};

export const daysUntilEndOfMonth = () => {
  const now = new Date();
  const end = endOfMonth(now);

  return differenceInDays(end, now);
};

export const isJanuary = () => {
  return new Date().getMonth() === 0;
};

export function parseAPIDate(date: string): Date | null {
  const parsed = parseISO(date);
  return isValid(parsed) ? parsed : null;
}
